/* https://brandplatform.smartphoto.com/general/molecules/collection-grid-body/ */
import {
	expressAddClass,
	expressEventListener,
	expressQuerySelector,
	expressQuerySelectorAll,
	expressRemoveClass,
	getSelfOrParentWithClassName,
	hasClass,
} from "../../common/html";
import { StoreType } from "../../services/storageService";
import { compositeDisposable, IDisposable } from "../../utils/disposable";
import { updateParameter } from "../../utils/querystring";

export interface IPageLoadedResult {
	isPageEmpty: boolean;
	noMorePagesToLoad: boolean;
}
export interface ICreateGridBodyDependencies {
	readonly maxSelectableItems: number;
	readonly isSelectAllActive?: () => boolean;
	readonly onItemClick?: (itemId: string, item: HTMLElement) => void;
	readonly onItemsSelect?: (itemIds: string[], items: HTMLElement[], isSelected: boolean) => void;
	readonly onNewItemClick?: () => void;
	readonly loadItems: (currentPage: number, store: StoreType, pageToken: string) => Promise<string>;
	readonly forceItemClickForSelector?: boolean;
	readonly updateUrl?: boolean;
	readonly itemsLoaded?: (pageLoadedResult: IPageLoadedResult) => void;
	readonly getImportedPhotosIds?: () => string[];
	readonly nrOfTotalItems: number;
}
export interface IGridBody {
	readonly gridBodyEl: HTMLElement;
	readonly getEnabledItems: () => HTMLElement[];
	readonly getSelectedItems: () => HTMLElement[];
	readonly getSelectedItemIds: () => string[];
	readonly getDeselectedItems: () => HTMLElement[];
	readonly getDeselectedItemIds: () => string[];
	readonly isItemEnabled: (item: HTMLElement) => boolean;
	readonly isItemDisabled: (item: HTMLElement) => boolean;
	readonly enableItem: (item: HTMLElement) => void;
	readonly disableItem: (item: HTMLElement) => void;
	readonly selectAllItems: (selectAll: boolean) => void;
	readonly getItems: () => HTMLElement[];
	readonly dispose: () => void;
	readonly removeAllItems: () => void;
	readonly loadNewPage: () => Promise<void>;
}

export async function createGridBody(containerEl: HTMLElement, deps: ICreateGridBodyDependencies): Promise<IGridBody> {
	const SELECTED_ITEM_CLASS = "m-collection-grid-item--selected";
	const DISABLED_ITEM_CLASS = "m-collection-grid-item--disabled";
	const ITEM_CLASS = "technical-grid-item";
	const ITEM_SELECTOR = "." + ITEM_CLASS;
	const GRID_ITEM_SELECTOR_CLASS = "technical-grid-item-selector";
	const ADD_ITEM_CLASS = "technical-grid-add";
	let itemEls: HTMLElement[];
	let startIndex = -1;
	let endIndex = -1;

	const itemDisposable: IDisposable = compositeDisposable([]);
	const store = StoreType[containerEl.dataset.store] || StoreType.eXpress;
	const {
		maxSelectableItems = 0,
		isSelectAllActive,
		onItemClick,
		onItemsSelect,
		onNewItemClick,
		loadItems,
		forceItemClickForSelector = false,
		updateUrl = true,
		itemsLoaded,
		getImportedPhotosIds,
	} = deps;

	const initItemEls = () => {
		itemEls = expressQuerySelectorAll<HTMLElement>(containerEl, ITEM_SELECTOR);
	};

	const gridBodyClickEventHandler = (e: MouseEvent) => {
		const currentTarget = e.target as HTMLElement;
		let parent = getSelfOrParentWithClassName<HTMLElement>(currentTarget, ADD_ITEM_CLASS);
		if (parent) {
			onNewItemClick();
			return;
		}

		e.preventDefault();
		parent = getSelfOrParentWithClassName<HTMLElement>(currentTarget, GRID_ITEM_SELECTOR_CLASS);
		const gridItemEl = getSelfOrParentWithClassName<HTMLElement>(currentTarget, ITEM_CLASS);
		const swapIndexes = () => ([startIndex, endIndex] = [endIndex, startIndex]);
		if (!gridItemEl) return;
		const targetSelection = !isItemSelected(gridItemEl);

		if (e.shiftKey) {
			if (startIndex === -1) {
				startIndex = itemEls.indexOf(gridItemEl);
				endIndex = -1;
				if (parent) onSelectItemClickEvent(e, itemEls[startIndex], targetSelection);
				else selectItems([itemEls[startIndex]], targetSelection);
			} else {
				const indexOfClicked = itemEls.indexOf(gridItemEl);
				if (startIndex === indexOfClicked && endIndex === -1) {
					if (parent) onSelectItemClickEvent(e, gridItemEl, targetSelection);
					else selectItems([gridItemEl], targetSelection);
					startIndex = isItemSelected(gridItemEl) ? indexOfClicked : -1;
				} else if (startIndex === indexOfClicked && endIndex !== -1) {
					if (parent)
						itemEls.slice(startIndex, endIndex + 1).forEach((itemEl: HTMLElement) => {
							onSelectItemClickEvent(e, itemEl, targetSelection);
						});
					else selectItems(itemEls.slice(startIndex, endIndex + 1), targetSelection);
					startIndex = -1;
					endIndex = -1;
				} else if (endIndex === -1) {
					let swapped = false;
					endIndex = indexOfClicked;
					if (endIndex < startIndex) {
						swapIndexes();
						swapped = true;
					}
					if (parent)
						itemEls.slice(swapped ? startIndex : startIndex + 1, endIndex + 1).forEach((itemEl: HTMLElement) => {
							onSelectItemClickEvent(e, itemEl, targetSelection);
						});
					else selectItems(itemEls.slice(swapped ? startIndex : startIndex + 1, endIndex + 1), targetSelection);
				} else {
					if (indexOfClicked > startIndex && indexOfClicked < endIndex) {
						if (endIndex < startIndex) swapIndexes();
						const oldEndIndex = endIndex;
						endIndex = indexOfClicked;
						if (parent)
							itemEls.slice(indexOfClicked, oldEndIndex + 1).forEach((itemEl: HTMLElement) => {
								onSelectItemClickEvent(e, itemEl, targetSelection);
							});
						else selectItems(itemEls.slice(indexOfClicked, oldEndIndex + 1), targetSelection);
					} else if (indexOfClicked > endIndex) {
						const oldEndIndex = endIndex;
						endIndex = indexOfClicked;
						if (parent)
							itemEls.slice(oldEndIndex + 1, endIndex + 1).forEach((itemEl: HTMLElement) => {
								onSelectItemClickEvent(e, itemEl, targetSelection);
							});
						else selectItems(itemEls.slice(oldEndIndex + 1, endIndex + 1), targetSelection);
					} else {
						startIndex = itemEls.indexOf(gridItemEl);
						endIndex = -1;
						if (parent)
							itemEls.slice(startIndex, endIndex + 1).forEach((itemEl: HTMLElement) => {
								onSelectItemClickEvent(e, itemEl, targetSelection);
							});
						else selectItems(itemEls.slice(startIndex, endIndex + 1), targetSelection);
					}
				}
			}
		} else {
			if (parent) onSelectItemClickEvent(e, gridItemEl, targetSelection);
			else {
				if (onItemClick) {
					onItemClickEvent(e, gridItemEl);
				} else {
					onSelectItemClickEvent(e, gridItemEl, targetSelection);
				}
			}
			startIndex = itemEls.indexOf(gridItemEl);
			endIndex = -1;
		}
	};

	const onItemClickEvent = (ev: MouseEvent, itemEl: HTMLElement) => {
		if (getSelectedItems().length > 0) {
			selectItems([itemEl], !isItemSelected(itemEl));
			return;
		}
		onItemClick && onItemClick(getItemId(itemEl), itemEl);
	};

	const onSelectItemClickEvent = (ev: MouseEvent, itemEl: HTMLElement, isSelected?: boolean) => {
		ev.preventDefault();
		ev.stopPropagation();
		selectItems([itemEl], isSelected);
	};

	const initItemTile = (itemEl: HTMLElement, selected?: boolean, hide?: boolean) => {
		const img = expressQuerySelector<HTMLImageElement>(itemEl, "img", true);

		if (hide) {
			disableItem(itemEl);
			itemEl.classList.add("u-hide");
		} else if (selected) {
			selectItems([itemEl], selected);
		}

		const onImgLoaded = () => {
			expressRemoveClass(itemEl, "skeleton-loading");
			resolvePromise();
		};

		const onImgError = () => {
			img.src = containerEl.dataset.backupimage;
		};

		let resolvePromise;
		return new Promise<void>((resolve) => {
			resolvePromise = resolve;

			itemDisposable.add(expressEventListener(img, "error", onImgError));
			if (!img.complete) {
				itemDisposable.add(expressEventListener(img, "load", onImgLoaded));
			} else {
				if (img.naturalHeight === 0 || img.naturalWidth === 0) onImgError();
				onImgLoaded();
			}
		});
	};

	const loadNewPage = async () => {
		if (!loadItems) return;

		const pageLoadedResult = { isPageEmpty: false, noMorePagesToLoad: false } as IPageLoadedResult;

		const pageNrToLoad = getCurrentPage() + 1;
		let pageToken = "";

		// Get the pageToken of the last element
		if (itemEls.length > 0) pageToken = itemEls[itemEls.length - 1].dataset.pagetoken;

		if (!pageToken) pageToken = pageNrToLoad.toString();

		// Check if the page is already loading
		if (containerEl.dataset.loadingPageToken == pageToken) return;

		// Mark the loadingElem as 'loading next page'
		containerEl.dataset.loadingPageToken = pageToken;

		// Check is more pages need to be loaded
		if (pageToken == "__NoMorePages__") {
			pageLoadedResult.noMorePagesToLoad = true;
		} else {
			const res = await loadItems(pageNrToLoad, store, pageToken);

			setCurrentPage(pageNrToLoad);

			if (!res || !res.trim()) pageLoadedResult.isPageEmpty = true;
			else await handleFetchedItems(res);
		}

		itemsLoaded && itemsLoaded(pageLoadedResult);
	};

	const handleFetchedItems = async (res: string) => {
		if (updateUrl) {
			let url = location.href;
			url = updateParameter("currentpage", getCurrentPage() + "", url.toLowerCase());
			history.replaceState(null, "", url.toLowerCase());
		}

		containerEl.insertAdjacentHTML("beforeend", res);
		initItemEls();

		const promises: Promise<any>[] = [];
		const importedItemIds = getImportedPhotosIds ? getImportedPhotosIds() : [];
		const allSelectedActive = isSelectAllActive && isSelectAllActive();
		itemEls.forEach((el) => {
			const itemId = getItemId(el);
			if (res.search(itemId) >= 0) {
				const hide = importedItemIds.includes(itemId);

				const promise = initItemTile(el, allSelectedActive, hide);

				if (hide) return;
				promises.push(promise);
			}
		});

		// No promises? So no item are processed and the selectedItemsCount isn't updated
		// Update it by manually triggering an itemSelect
		if (promises.length == 0) {
			const firstItem = itemEls[0];
			if (firstItem) {
				const firstItemId = getItemId(firstItem);
				onItemsSelect && onItemsSelect([firstItemId], [firstItem], isItemSelected(firstItem));
			}
		}

		return await Promise.all(promises);
	};

	const init = async () => {
		initItemEls(); // (re-)init the itemEls. Important when refreshing the grid
		const allSelected = isSelectAllActive();
		itemEls.forEach((itemEl) => initItemTile(itemEl, allSelected));
		itemDisposable.add(expressEventListener(containerEl, "click", gridBodyClickEventHandler));
		containerEl.style.userSelect = "none";
	};
	const selectItems = (itemElsToSelect: HTMLElement[], isSelected?: boolean) => {
		if (maxSelectableItems > 0 && getSelectedItems().length >= maxSelectableItems) {
			let nrOfItemsToDeselect = getSelectedItems().length - maxSelectableItems + 1;
			getSelectedItems().forEach((el) => {
				if (nrOfItemsToDeselect == 0) return;
				expressRemoveClass(el, SELECTED_ITEM_CLASS);
				nrOfItemsToDeselect--;
			});
		}

		itemElsToSelect.forEach((itemEl) => {
			// Item disabled > select not allowed
			if (isItemDisabled(itemEl)) {
				itemEl.classList.remove(SELECTED_ITEM_CLASS);
				return;
			}

			itemEl.classList.toggle(SELECTED_ITEM_CLASS, isSelected);
		});

		const itemIds = itemElsToSelect.map((item) => getItemId(item));
		onItemsSelect && onItemsSelect(itemIds, itemElsToSelect, isSelected);
		if (forceItemClickForSelector && onItemClick) {
			itemElsToSelect.forEach((itemElToSelect) => {
				return onItemClick(getItemId(itemElToSelect), itemElToSelect);
			});
		}
	};

	const getItemId = (itemEl: HTMLElement) => {
		return itemEl.dataset.itemid;
	};

	const selectAllItems = (selected: boolean): void => {
		const currentItemEls = itemEls.filter((i) => isItemSelected(i) == !selected);
		selectItems(currentItemEls, selected);
	};

	const enableItem = (itemEl: HTMLElement) => {
		expressRemoveClass(itemEl, DISABLED_ITEM_CLASS);
	};
	const disableItem = (itemEl: HTMLElement) => {
		expressRemoveClass(itemEl, SELECTED_ITEM_CLASS);
		expressAddClass(itemEl, DISABLED_ITEM_CLASS);
	};
	const isItemEnabled = (itemEl: HTMLElement) => {
		if (itemEl) return !isItemDisabled(itemEl);
		return false;
	};
	const isItemDisabled = (itemEl: HTMLElement) => {
		if (itemEl) return hasClass(itemEl, DISABLED_ITEM_CLASS);
		return false;
	};
	const isItemSelected = (itemEl: HTMLElement) => {
		if (itemEl) return hasClass(itemEl, SELECTED_ITEM_CLASS);
		return false;
	};

	const getEnabledItems = () => {
		return itemEls.filter((i) => isItemEnabled(i));
	};
	const getSelectedItems = () => {
		return itemEls.filter((i) => isItemSelected(i));
	};

	const getSelectedItemIds = () => {
		return getSelectedItems().map((i) => getItemId(i));
	};

	const getDeselectedItems = () => {
		return itemEls.filter((i) => !isItemSelected(i));
	};
	const getDeselectedItemIds = () => {
		return getDeselectedItems().map((i) => getItemId(i));
	};

	const removeAllItems = () => {
		const items = expressQuerySelectorAll(containerEl, ITEM_SELECTOR);
		items.forEach((i) => i.remove());

		initItemEls();
	};

	const dispose = () => {
		itemDisposable.dispose();
	};

	const setCurrentPage = (pageNr: number) => (containerEl.dataset.page = pageNr.toString());
	const getCurrentPage = () => parseInt(containerEl.dataset.page, 10) || 0;

	await init();

	return {
		gridBodyEl: containerEl,
		getEnabledItems: getEnabledItems,
		getSelectedItems,
		getSelectedItemIds,
		getDeselectedItems,
		getDeselectedItemIds,
		isItemEnabled,
		isItemDisabled,
		enableItem,
		disableItem,
		dispose,
		selectAllItems,
		getItems: () => {
			return itemEls;
		},
		removeAllItems,
		loadNewPage,
	} as IGridBody;
}
